import { useId, useState } from 'react';
import styles from './Calendar.module.scss';
import dayjs from 'dayjs';

interface IProps {
  value?: string;
  min?: string;
  max?: string;
  // eslint-disable-next-line no-unused-vars
  onSelect: (value: string) => void;
  close: () => void;
}

const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export function Calendar({ value, min, max, onSelect, close }: IProps) {
  const [month, setMonth] = useState(() => {
    let initialMonth = dayjs(value).startOf('month');
    if (min && initialMonth.isBefore(dayjs(min), 'month')) initialMonth = dayjs(min).startOf('month');
    if (max && initialMonth.isAfter(dayjs(max), 'month')) initialMonth = dayjs(max).startOf('month');
    return initialMonth;
  });
  const previousMonthDisabled = Boolean(min && !month.isAfter(dayjs(min), 'month'));
  const nextMonthDisabled = Boolean(max && !month.isBefore(dayjs(max), 'month'));
  const titleId = useId();

  const chooseDate = (date: string) => {
    if ((min && date < min) || (max && date > max)) return;
    if (date !== value) onSelect(date);
    close();
  };

  const days = Array.from({ length: month.daysInMonth() }, (_, index) => month.date(index + 1));
  const today = dayjs().format('YYYY-MM-DD');

  return (
    <>
      <div className={styles.header}>
        <button type="button" aria-label="Previous month" disabled={previousMonthDisabled} onClick={() => setMonth((current) => current.subtract(1, 'month'))}>
          ‹
        </button>
        <span id={titleId} aria-live="polite">
          {month.format('MMMM YYYY')}
        </span>
        <button type="button" aria-label="Next month" disabled={nextMonthDisabled} onClick={() => setMonth((current) => current.add(1, 'month'))}>
          ›
        </button>
      </div>
      <div className={styles.days} role="group" aria-labelledby={titleId}>
        {weekdays.map((day) => (
          <span key={day} aria-hidden="true" className={styles.weekday}>
            {day}
          </span>
        ))}
        {Array.from({ length: month.day() }, (_, index) => (
          <span key={`blank-${index}`} aria-hidden="true" />
        ))}
        {days.map((day) => {
          const date = day.format('YYYY-MM-DD');
          const selected = date === value;
          const dayClassName = `${styles.day} ${date === today ? styles.today : ''} ${selected ? styles.selected : ''}`;
          const unavailable = Boolean((min && date < min) || (max && date > max));
          return (
            <button key={date} type="button" className={dayClassName} aria-label={day.format('MMMM D, YYYY')} aria-pressed={selected} disabled={unavailable} onClick={() => chooseDate(date)}>
              {day.date()}
            </button>
          );
        })}
      </div>
    </>
  );
}
